/**
 * Snak response capture.
 *
 * Every answer is stored against an anonymous session id so the prior
 * (TRACKING_PRIOR) and the updated belief (PRECISION_UPDATE) can be read as a
 * pair, not just as two independent tallies. Responses are persisted locally and
 * also emitted on `window` as `snak:response` so a real analytics endpoint can
 * subscribe without touching the experience.
 */

export type QuestionId = 'TRACKING_PRIOR' | 'PRECISION_UPDATE'

export interface StoredResponse {
  sessionId: string
  question: QuestionId
  answer: string
  at: number
}

const STORAGE_KEY = 'solventum.snak.precision-grip.responses.v1'

/** Below this, peers are still being counted — never show invented percentages. */
export const MIN_RESPONSES = 20

export function createSessionId(): string {
  return `s-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`
}

export function readResponses(): StoredResponse[] {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) return []
    const parsed: unknown = JSON.parse(raw)
    return Array.isArray(parsed) ? (parsed as StoredResponse[]) : []
  } catch {
    return []
  }
}

export function recordResponse(response: StoredResponse): void {
  try {
    const next = [...readResponses(), response]
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next))
  } catch {
    // Storage unavailable — the event below is still emitted.
  }

  try {
    window.dispatchEvent(
      new CustomEvent<StoredResponse>('snak:response', { detail: response }),
    )
  } catch {
    // Nothing listening.
  }
}

export interface BenchmarkRow {
  answer: string
  count: number
  /** 0–1. Only meaningful when the benchmark is ready. */
  share: number
}

export interface Benchmark {
  /** False while there are too few responses to report a split. */
  ready: boolean
  total: number
  needed: number
  rows: BenchmarkRow[]
}

export function benchmarkFor(
  question: QuestionId,
  options: string[],
): Benchmark {
  const responses = readResponses().filter((row) => row.question === question)
  const total = responses.length

  const rows = options.map((answer) => {
    const count = responses.filter((row) => row.answer === answer).length
    return {
      answer,
      count,
      share: total > 0 ? count / total : 0,
    }
  })

  return {
    ready: total >= MIN_RESPONSES,
    total,
    needed: Math.max(0, MIN_RESPONSES - total),
    rows,
  }
}

/** Prior → updated belief, paired by session. Read by reporting, not by the UI. */
export function beliefMovement(): { prior: string; update: string }[] {
  const responses = readResponses()
  const sessions = Array.from(new Set(responses.map((row) => row.sessionId)))

  return sessions
    .map((sessionId) => {
      const rows = responses.filter((row) => row.sessionId === sessionId)
      const prior = rows.find((row) => row.question === 'TRACKING_PRIOR')
      const update = rows.find((row) => row.question === 'PRECISION_UPDATE')
      if (!prior || !update) return null
      return { prior: prior.answer, update: update.answer }
    })
    .filter((pair): pair is { prior: string; update: string } => pair !== null)
}
